/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { Logger } from '@feature-hub/logger';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { AudiVideoServiceInterfaceV1 } from '@oneaudi/video-service';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import type { I18NServiceV1 } from '@oneaudi/i18n-service';
import type { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import type { VideoPlayerConfig } from '../types';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import ContextProvider from './context/Context';
import App from './FeatureApp';
import { isTrackingDisabled } from './utils/debugging';
import { createUniversalState, type UniversalState } from './utils/createUniversalState';
import { UniversalStateContext } from './context/UniversalStateContext';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined;
  readonly 's2:logger': Logger;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 'audi-video-service': AudiVideoServiceInterfaceV1;
  readonly 'locale-service': LocaleServiceV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 'dbad:audi-i18n-service': I18NServiceV1;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
}

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      'audi-video-service': '1.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
    },
  },
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'locale-service': '^1.0.0',
    },
    externals: {
      '@oneaudi/unified-web-common': '*',
      react: '^17.0.2 || ^18.2.0',
      'react-dom': '^17.0.2 || ^18.2.0',
      'styled-components': '*',
    },
  },

  create: ({
    config,
    featureServices,
    featureAppId,
    featureAppName,
  }: FeatureAppEnvironment<FeatureServiceDependencies, VideoPlayerConfig>) => {
    const loggerService = featureServices['s2:logger'];
    const serializedStateManager = featureServices['s2:serialized-state-manager'];
    const localeService = featureServices['gfa:locale-service'];
    const i18nService = featureServices['dbad:audi-i18n-service'];
    const asyncSsrManager = featureServices['s2:async-ssr-manager'];
    loggerService?.info('Feature App created.');

    const trackingService = !isTrackingDisabled()
      ? featureServices['audi-tracking-service']
      : undefined;
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
      trackingService.featureAppId = featureAppId;
    }
    const serializedState = serializedStateManager?.getSerializedState();

    let universalState: UniversalState | undefined = serializedState
      ? (JSON.parse(serializedState) as UniversalState)
      : undefined;

    serializedStateManager?.register(() => JSON.stringify(universalState));

    const loadingPromise =
      universalState === undefined
        ? createUniversalState(localeService, i18nService, loggerService).then((state) => {
            universalState = state;
          })
        : undefined;

    asyncSsrManager?.scheduleRerender(loadingPromise);

    const videoService = featureServices['audi-video-service'];

    return {
      loadingPromise,
      render: () => {
        return (
          <UniversalStateContext.Provider value={universalState}>
            <ContextProvider
              featureAppEnv={{ featureAppId, featureAppName }}
              loggerService={loggerService}
              trackingService={trackingService}
              videoService={videoService}
              localeService={localeService}
            >
              <App id="featureAppId" config={config} />
            </ContextProvider>
          </UniversalStateContext.Provider>
        );
      },
    };
  },
};

export default featureAppDefinition;
